import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Eye,
  FileText,
  PackageCheck,
  Truck,
} from 'lucide-react';
import type { Order, OrderStatus, OrderStatusCounts, TrackingCarrier } from '@rosita/shared';
import { ORDER_STATUSES } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, apiBaseUrl, extractError } from '../../lib/api';
import { formatDate, formatEur } from '../../lib/format';
import { OrderStatusBadge } from '../../components/admin/OrderStatusBadge';
import { ActionMenu, type ActionItem } from '../../components/admin/ActionMenu';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { ShipOrderDialog } from '../../components/admin/ShipOrderDialog';
import { useToast } from '../../components/admin/Toast';

interface OrderListResponse {
  items: Order[];
  total: number;
  totalPages: number;
  counts: OrderStatusCounts;
}

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';
const thRight = `${thClass} text-right`;

export default function AdminOrdersPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState<OrderStatusCounts>({});
  const [pendingPrepare, setPendingPrepare] = useState<Order | null>(null);
  const [shippingOrder, setShippingOrder] = useState<Order | null>(null);
  const [searchInput, setSearchInput] = useState(sp.get('search') ?? '');
  const [refreshKey, setRefreshKey] = useState(0);
  const searchQuery = sp.get('search') ?? '';
  const statusFilter = sp.get('status') ?? 'all';
  const sortField = sp.get('sort') ?? 'createdAt';
  const sortDir = sp.get('dir') ?? 'desc';

  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const params = new URLSearchParams(sp);
        if (!params.get('page')) params.set('page', '1');
        if (!params.get('limit')) params.set('limit', '20');
        const response = await api.get<OrderListResponse>(`/admin/orders?${params.toString()}`);
        setOrders(response.data.items);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
        setCounts(response.data.counts);
        // Stale deep link past the last page → land on the last page instead.
        const page = Number(params.get('page'));
        if (
          response.data.items.length === 0 &&
          response.data.total > 0 &&
          page > response.data.totalPages
        ) {
          const next = new URLSearchParams(sp);
          next.set('page', String(response.data.totalPages));
          setSp(next, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [sp, setSp, refreshKey]);

  // Keep the input in sync when the URL changes elsewhere (back/forward nav).
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Debounce typing into the URL so we don't fetch on every keystroke.
  const debounceReady = useRef(false);
  useEffect(() => {
    if (!debounceReady.current) {
      debounceReady.current = true;
      return;
    }
    if (searchInput === searchQuery) return;
    const timer = setTimeout(() => {
      setSp((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.set('page', '1');
        return next;
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, setSp]);

  // setSp with an unchanged URL is a no-op (React Router memoises searchParams
  // on location.search), so refreshing after a mutation needs its own trigger.
  const refresh = (): void => setRefreshKey((k) => k + 1);

  const setStatus = (value: string): void => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('status');
      else next.set('status', value);
      next.set('page', '1');
      return next;
    });
  };

  const toggleSort = (field: string): void => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const curField = prev.get('sort') ?? 'createdAt';
      const curDir = prev.get('dir') ?? 'desc';
      if (curField === field) {
        next.set('dir', curDir === 'asc' ? 'desc' : 'asc');
      } else {
        next.set('sort', field);
        next.set('dir', 'asc');
      }
      next.set('page', '1');
      return next;
    });
  };

  const markPreparing = async (): Promise<void> => {
    if (!pendingPrepare) return;
    try {
      await api.patch(`/admin/orders/${pendingPrepare.orderNumber}/status`, {
        status: 'preparing',
      });
      toast.success("Encomenda marcada como 'A preparar'.");
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar o estado'));
    } finally {
      // Refresh on both paths — the order may have changed under us.
      setPendingPrepare(null);
      refresh();
    }
  };

  const shipOrder = async (carrier: TrackingCarrier, trackingNumber: string): Promise<void> => {
    if (!shippingOrder) return;
    try {
      await api.patch(`/admin/orders/${shippingOrder.orderNumber}/status`, {
        status: 'shipped',
        trackingCarrier: carrier,
        trackingNumber,
      });
      toast.success('Encomenda marcada como expedida.');
      setShippingOrder(null);
      refresh();
    } catch (err) {
      // Keep the dialog open so the admin can fix the input and retry.
      toast.error(extractError(err, 'Falha ao atualizar o estado'));
    }
  };

  const apiBase = apiBaseUrl;

  const rowActions = (o: Order): ActionItem[] => {
    const items: ActionItem[] = [
      {
        label: 'Ver encomenda',
        icon: Eye,
        onClick: () => navigate(`/admin/encomendas/${o.orderNumber}`),
      },
    ];
    if (o.status === 'paid') {
      items.push({
        label: "Marcar como 'A preparar'",
        icon: PackageCheck,
        onClick: () => setPendingPrepare(o),
      });
    }
    if (o.status === 'paid' || o.status === 'preparing') {
      items.push({
        label: "Marcar como 'Expedido'",
        icon: Truck,
        onClick: () => setShippingOrder(o),
      });
    }
    if (o.paymentStatus === 'paid') {
      items.push({
        label: 'Descarregar fatura',
        icon: FileText,
        onClick: () =>
          window.open(
            `${apiBase}/api/v1/admin/orders/${o.orderNumber}/invoice`,
            '_blank',
            'noopener'
          ),
      });
    }
    return items;
  };

  const sortIndicator = (field: string): JSX.Element => {
    if (sortField !== field)
      return <ChevronsUpDown aria-hidden className="h-3.5 w-3.5 opacity-40" />;
    return sortDir === 'asc' ? (
      <ChevronUp aria-hidden className="h-3.5 w-3.5" />
    ) : (
      <ChevronDown aria-hidden className="h-3.5 w-3.5" />
    );
  };

  const ariaSort = (field: string): 'ascending' | 'descending' | 'none' =>
    sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';

  const totalAll = Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0);
  const statusChips: { value: string; label: string }[] = [
    { value: 'all', label: 'Todas' },
    ...ORDER_STATUSES.map((s) => ({ value: s, label: t.orderStatus[s] })),
  ];

  return (
    <>
      <SEO title="Encomendas (admin)" noindex />
      <h1>Encomendas ({total})</h1>

      <div className="my-4 space-y-3">
        <input
          type="search"
          placeholder="Pesquisar por nº, cliente ou email…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={`${inputClass} w-full max-w-sm`}
        />
        <div
          role="group"
          aria-label="Filtrar por estado"
          className="flex flex-wrap items-center gap-1.5"
        >
          {statusChips.map((opt) => {
            const n =
              opt.value === 'all' ? totalAll : counts[opt.value as OrderStatus] ?? 0;
            const active = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatus(opt.value)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                  active
                    ? 'border-admin-primary bg-admin-primary text-white'
                    : 'border-admin-border bg-white text-admin-text-soft hover:bg-admin-surface-alt'
                }`}
              >
                {opt.label}
                <span
                  className={`tabular-nums text-xs ${
                    active
                      ? 'text-white/80'
                      : n === 0
                        ? 'text-admin-muted'
                        : 'text-admin-text-soft'
                  }`}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <p className="text-admin-text-soft">A carregar…</p>
      ) : (
        <>
          <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
            <thead>
              <tr className="border-b border-admin-border">
                <th className={thClass}>Nº</th>
                <th className={thClass}>Cliente</th>
                <th className={thClass} aria-sort={ariaSort('createdAt')}>
                  <button
                    type="button"
                    onClick={() => toggleSort('createdAt')}
                    className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-admin-text"
                  >
                    Data {sortIndicator('createdAt')}
                  </button>
                </th>
                <th className={thClass}>Estado</th>
                <th className={thRight} aria-sort={ariaSort('total')}>
                  <button
                    type="button"
                    onClick={() => toggleSort('total')}
                    className="ml-auto inline-flex items-center gap-1 uppercase tracking-wider hover:text-admin-text"
                  >
                    Total {sortIndicator('total')}
                  </button>
                </th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-admin-text-soft">
                    {searchQuery || statusFilter !== 'all'
                      ? 'Nenhuma encomenda corresponde aos filtros.'
                      : 'Ainda não tens encomendas.'}
                  </td>
                </tr>
              ) : null}
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={(e) => {
                    // Links and the actions menu handle their own clicks.
                    if ((e.target as HTMLElement).closest('a, button')) return;
                    navigate(`/admin/encomendas/${o.orderNumber}`);
                  }}
                  className="cursor-pointer border-b border-admin-border last:border-b-0 hover:bg-admin-surface-alt"
                >
                  <td className="p-3 font-mono text-sm">
                    <Link
                      to={`/admin/encomendas/${o.orderNumber}`}
                      className="text-admin-primary hover:underline"
                    >
                      {o.orderNumber}
                    </Link>
                  </td>
                  <td className="p-3">
                    <div>
                      {o.shippingFirstName} {o.shippingLastName}
                    </div>
                    {o.guestEmail ? (
                      <div className="text-xs text-admin-muted">{o.guestEmail}</div>
                    ) : null}
                  </td>
                  <td className="p-3 text-admin-text-soft">{formatDate(o.createdAt)}</td>
                  <td className="p-3">
                    <OrderStatusBadge status={o.status} />
                  </td>
                  <td className="p-3 text-right font-medium">{formatEur(o.total)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end">
                      <ActionMenu
                        label={`Ações para a encomenda ${o.orderNumber}`}
                        items={rowActions(o)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={Number(sp.get('page') ?? '1') <= 1}
                onClick={() => {
                  const next = new URLSearchParams(sp);
                  const page = Math.max(1, Number(sp.get('page') ?? '1') - 1);
                  next.set('page', String(page));
                  setSp(next);
                }}
                className={secondaryBtn}
              >
                Anterior
              </button>
              <span className="text-sm text-admin-text-soft">
                Página {sp.get('page') ?? '1'} de {totalPages}
              </span>
              <button
                type="button"
                disabled={Number(sp.get('page') ?? '1') >= totalPages}
                onClick={() => {
                  const next = new URLSearchParams(sp);
                  const page = Number(sp.get('page') ?? '1') + 1;
                  next.set('page', String(page));
                  setSp(next);
                }}
                className={secondaryBtn}
              >
                Seguinte
              </button>
            </div>
          ) : null}
        </>
      )}

      {pendingPrepare ? (
        <ConfirmDialog
          title="Marcar como 'A preparar'"
          message={
            <>
              A encomenda{' '}
              <strong className="font-semibold text-admin-text">
                {pendingPrepare.orderNumber}
              </strong>{' '}
              vai passar a &lsquo;A preparar&rsquo; e a cliente é notificada por email.
            </>
          }
          confirmLabel="Confirmar"
          busyLabel="A atualizar…"
          onConfirm={markPreparing}
          onClose={() => setPendingPrepare(null)}
        />
      ) : null}

      {shippingOrder ? (
        <ShipOrderDialog
          orderNumber={shippingOrder.orderNumber}
          onConfirm={shipOrder}
          onClose={() => setShippingOrder(null)}
        />
      ) : null}
    </>
  );
}
